/**
 * Phase 2 gate — true only in local `npm start` / development builds.
 * CRA replaces process.env.NODE_ENV at build time, so production bundles
 * treat the gated code as dead and tree-shake it away.
 *
 * Gated features (Phase 2 — post-launch):
 *   • AI Agent        — conversational compliance assistant
 *   • Business Platform — Company Marketplace, Valuation Tools, Due Diligence
 */
export const IS_DEV = process.env.NODE_ENV === "development";

// Application constants for the new UI

export const ROUTES = {
  LOGIN: `/login`,
  RESET_PASSWORD: `/reset-password`,
  MAGIC_LOGIN: `/magic-login`,
  DASHBOARD: `/dashboard`,
  ACCEPT_INVITATION: `/accept-invitation`,
  // Public landing for the Reg 3(2B) notice link. Must stay outside any auth
  // wrapper: external recipients have no account here.
  SDD_NOTICE_ACK: `/sdd/notice/:token`,
  // Our own legal pages. Public: a payment gateway will not activate an
  // account until it can open these without signing in. Note PRIVACY below
  // is a different thing entirely — the customer's DPDP toolkit.
  LEGAL: `/legal/:policyId`,
  TERMS: `/legal/terms`,
  PRIVACY_POLICY: `/legal/privacy-policy`,
  REFUND_POLICY: `/legal/refund-policy`,
  CONTACT: `/legal/contact`,
  COMPANIES: `/companies`,
  ALERTS: `/alerts`,
  // NOT `/compliance` — src/App.js already routes that to the public marketing
  // page (Components/Compliance), and the legacy router wins, so the new-ui page
  // never renders. Any new route here must be checked against App.js first.
  COMPLIANCE: `/compliance-checklist`,
  // Company Types
  COMPANY_TYPES: `/company-types`,
  PVT_LIMITED: `/pvt-limited`,
  LLP: `/llp`,
  PUBLIC_CO: `/public-company`,
  OPC: `/opc`,
  NGO: `/ngo`,
  // AI & Documents
  AGENT: `/agent`,
  AGENT_HISTORY: `/agent/history`,
  MINUTES: `/minutes`,
  MEETINGS: `/meetings`,
  RESOLUTIONS: `/resolutions`,
  MY_DOCUMENTS: `/my-documents`,
  DOC_LIBRARY: `/documents`,
  // Collaborator Management
  MANAGE_COLLABORATORS: `/manage-collaborators`,
  PROFESSIONAL_DASHBOARD: `/professional-dashboard`,
  // Filing & Calendar
  ANNUAL_FILING: `/annual-filing`,
  AGM_CALC: `/agm-calculator`,
  CALENDAR: `/calendar`,
  MCA_FORMS: `/mca-forms`,
  MCA_PREPARE: `/mca-forms/prepare`,
  // The eleven statutory registers under the Companies Act, 2013
  REGISTERS: `/registers`,
  // SEBI PIT — Structured Digital Database (Reg 3(5)/3(6))
  SDD: `/sdd`,
  REPORT_TEMPLATES: `/report-templates`,
  MY_TEMPLATES: `/my-templates`,
  REPORT_GENERATOR: `/report-generator`,
  // Business Platform
  MARKETPLACE: `/marketplace`,
  VALUATION: `/valuation`,
  DUE_DILIGENCE: `/due-diligence`,
  // Privacy & Security
  PRIVACY: `/privacy`,
  DP_CONTROLS: `/data-controls`,
  MY_AI_USAGE: `/my-ai-usage`,
  // Account
  PROFILE: `/profile`,
  PACKAGES: `/packages`,
  SUPPORT: `/support`,
  // Admin
  ADMIN_DASHBOARD: `/admin`,
  ADMIN_USERS: `/admin/users`,
  ADMIN_SERVICES: `/admin/services`,
  // Service health across client SDD registers — metadata only, never content.
  ADMIN_SDD: `/admin/sdd`,
  // AI Control Plane — dedicated section (own sidebar group), one route per screen.
  ADMIN_AI: `/admin/ai`,
  ADMIN_AI_OVERVIEW: `/admin/ai/overview`,
  ADMIN_AI_PROVIDERS: `/admin/ai/providers`,
  ADMIN_AI_USAGE_COST: `/admin/ai/usage`,
  ADMIN_AI_BUDGET: `/admin/ai/budget`,
  ADMIN_AI_SETTINGS: `/admin/ai/settings`,
  ADMIN_AI_GOVERNANCE: `/admin/ai/governance`,
  ADMIN_AI_TEMPLATES: `/admin/ai/templates`,
  ADMIN_AI_ANSWER_REVIEW: `/admin/ai/answer-review`,
  ADMIN_AI_TRACES: `/admin/ai/traces`,
  ADMIN_ROLES: `/admin/roles`,
  ADMIN_ENTITLEMENTS: `/admin/entitlements`,
  ADMIN_COMPLIANCE_RULES: `/admin/compliance-rules`,
  ADMIN_COMPANIES: `/admin/companies`,
  ADMIN_ORDERS: `/admin/orders`,
  ADMIN_AUDIT_LOGS: `/admin/audit-logs`,
  ADMIN_ERROR_LOGS: `/admin/error-logs`,
  ADMIN_FLAG_INBOX: `/admin/flag-inbox`,
  ADMIN_ALERTS_FEED: `/admin/security/alerts`,
  ADMIN_SUPPORT_QUEUE: `/admin/support-queue`,
  // Living design-system reference (all users in dev; super_admin in prod)
  DESIGN_SYSTEM: `/design-system`,
};

export const USER_ROLES = {
  PROFESSIONAL: "professional",
  CORPORATE: "corporate",
  ADMIN: "admin",
  SUPER_ADMIN: "super_admin",
};

// Admin-specific navigation sections
export const ADMIN_NAV_SECTIONS = [
  {
    label: "Admin Overview",
    items: [
      {
        id: "admin-dashboard",
        label: "Admin Dashboard",
        icon: "⚙️",
        route: ROUTES.ADMIN_DASHBOARD,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-users",
        label: "User Management",
        icon: "👥",
        route: ROUTES.ADMIN_USERS,
        roles: ["admin", "super_admin"],
      },
    ],
  },
  {
    label: "Data Management",
    items: [
      {
        id: "admin-companies",
        label: "Companies",
        icon: "🏢",
        route: ROUTES.ADMIN_COMPANIES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-orders",
        label: "Orders",
        icon: "📦",
        route: ROUTES.ADMIN_ORDERS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-support",
        label: "Support Queue",
        icon: "🎧",
        route: ROUTES.ADMIN_SUPPORT_QUEUE,
        roles: ["admin", "super_admin"],
      },
    ],
  },
  {
    label: "System Management",
    items: [
      {
        id: "admin-services",
        label: "Services",
        icon: "🔧",
        route: ROUTES.ADMIN_SERVICES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-roles",
        label: "Roles & Permissions",
        icon: "🔐",
        route: ROUTES.ADMIN_ROLES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-entitlements",
        label: "Entitlements",
        icon: "🎚️",
        route: ROUTES.ADMIN_ENTITLEMENTS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-compliance-rules",
        label: "Compliance Rule Book",
        icon: "📕",
        route: ROUTES.ADMIN_COMPLIANCE_RULES,
        roles: ["admin", "super_admin"],
      },
    ],
  },
  {
    // Dedicated AI section — consolidates all AI operations. Each item is one
    // screen of the AIControlPlanePage (route-driven via /admin/ai/:screen).
    label: "AI Control Plane",
    items: [
      {
        id: "admin-ai-overview",
        label: "Overview",
        icon: "🎛️",
        route: ROUTES.ADMIN_AI_OVERVIEW,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-ai-providers",
        label: "Providers & Models",
        icon: "🤖",
        route: ROUTES.ADMIN_AI_PROVIDERS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-ai-usage",
        label: "Usage & Cost",
        icon: "📊",
        route: ROUTES.ADMIN_AI_USAGE_COST,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-ai-budget",
        label: "Budget & Caps",
        icon: "💰",
        route: ROUTES.ADMIN_AI_BUDGET,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-ai-settings",
        label: "Settings",
        icon: "🎚️",
        route: ROUTES.ADMIN_AI_SETTINGS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-ai-governance",
        label: "Governance",
        icon: "🛡️",
        route: ROUTES.ADMIN_AI_GOVERNANCE,
        roles: ["super_admin"],
      },
      {
        // Super-admin only — mirrors Governance above. Firm Templates exposes
        // per-user recipe contents + smoke-failure attribution; the backend
        // (getFirmTemplates) is the real gate, this just hides the tab.
        id: "admin-ai-templates",
        label: "Firm Templates",
        icon: "📋",
        route: ROUTES.ADMIN_AI_TEMPLATES,
        roles: ["super_admin"],
      },
      {
        // Thumbs-down replies for an expert to judge; corrected ones become test cases.
        id: "admin-ai-answer-review",
        label: "Answer Review",
        icon: "✅",
        route: ROUTES.ADMIN_AI_ANSWER_REVIEW,
        roles: ["super_admin", "admin"],
      },
      {
        // How Vee turns ran: speed, tokens, tool health. Timings and counts only.
        id: "admin-ai-traces",
        label: "Traces",
        icon: "📈",
        route: ROUTES.ADMIN_AI_TRACES,
        roles: ["super_admin", "admin"],
      },
    ],
  },
  {
    // The one-stop security surface: audit trail, error stream, user flags,
    // lockouts, and the automated admin-alerts feed.
    label: "Security",
    items: [
      // Audit and Error logs share AuditLogsTab; the two nav entries deep-link
      // into its audit / errors modes (the in-page toggle still works).
      {
        id: "admin-audit",
        label: "Audit Logs",
        icon: "🛡️",
        route: ROUTES.ADMIN_AUDIT_LOGS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-sdd",
        label: "SDD Monitoring",
        icon: "🔒",
        route: ROUTES.ADMIN_SDD,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-errors",
        label: "Error Logs",
        icon: "🐞",
        route: ROUTES.ADMIN_ERROR_LOGS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-flags",
        label: "Flag Inbox",
        icon: "🚩",
        route: ROUTES.ADMIN_FLAG_INBOX,
        roles: ["super_admin"],
      },
      {
        id: "admin-lockouts",
        label: "Account Lockouts",
        icon: "🔒",
        route: `${ROUTES.ADMIN_USERS}?filter=locked`,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-alerts",
        label: "Admin Alerts",
        icon: "🔔",
        route: ROUTES.ADMIN_ALERTS_FEED,
        roles: ["super_admin"],
      },
    ],
  },
];

export const NAV_SECTIONS = [
  {
    label: "Overview",
    items: [
      {
        id: "dashboard",
        label: "Dashboard",
        icon: "🏠",
        route: ROUTES.DASHBOARD,
        roles: ["professional", "corporate"],
      },
      {
        id: "companies",
        label: "Companies",
        icon: "🏢",
        route: ROUTES.COMPANIES,
        roles: ["professional", "corporate"],
        badgeKey: "totalCompanies",
      },
      {
        id: "compliance",
        label: "Compliance",
        icon: "📋",
        route: ROUTES.COMPLIANCE,
        roles: ["professional", "corporate"],
      },
      {
        id: "alerts",
        label: "Alerts & Dues",
        icon: "🔔",
        route: ROUTES.ALERTS,
        roles: ["professional", "corporate"],
        badgeKey: "criticalAlerts",
        badgeColor: "red",
      },
    ],
  },
  // Drafting tools are grouped by interaction model rather than by document
  // domain. "Drafting Studio" holds the full-page guided builders (fill a form,
  // refine a single document in a live editor). "Templates & Catalogs" holds the
  // browse-a-gallery-then-AI-draft pickers. "Saved Documents" is the archive
  // every tool above writes into — kept apart so it reads as a destination, not
  // another generator.
  {
    label: "Drafting Studio",
    items: [
      {
        id: "agent",
        // Vee is the heart of the product, so its menu entry is named for it
        // and drawn apart from the rest (see Sidebar: `highlight`, `tag`).
        label: "Vee",
        tag: "AI",
        highlight: true,
        icon: "🤖",
        route: ROUTES.AGENT,
        roles: ["professional", "corporate"],
      },
      {
        id: "minutes",
        label: "Minutes of Meeting",
        icon: "📋",
        route: ROUTES.MINUTES,
        roles: ["professional", "corporate"],
      },
      {
        // Distinct from "Minutes of Meeting" above, which drafts from a typed
        // form. This is the meeting itself — scheduled, recorded, transcribed,
        // then minuted from what was actually said.
        id: "meetings",
        label: "Board Meetings",
        icon: "🎙️",
        route: ROUTES.MEETINGS,
        roles: ["professional", "corporate"],
      },
      {
        id: "reportgenerator",
        label: "Report Generator",
        icon: "🧾",
        route: ROUTES.REPORT_GENERATOR,
        roles: ["professional", "corporate"],
      },
    ],
  },
  {
    label: "Templates & Catalogs",
    items: [
      {
        id: "resolutions",
        label: "Resolutions",
        icon: "📜",
        route: ROUTES.RESOLUTIONS,
        roles: ["professional", "corporate"],
      },
      {
        id: "doclibrary",
        label: "Document Library",
        icon: "🗂️",
        route: ROUTES.DOC_LIBRARY,
        roles: ["professional", "corporate"],
      },
      {
        id: "reporttemplates",
        label: "Report Templates",
        icon: "📊",
        route: ROUTES.REPORT_TEMPLATES,
        roles: ["professional", "corporate"],
      },
      {
        id: "mytemplates",
        label: "My Templates",
        icon: "⭐",
        route: ROUTES.MY_TEMPLATES,
        roles: ["professional", "corporate"],
      },
    ],
  },
  {
    label: "Saved Documents",
    items: [
      {
        id: "mydocs",
        label: "My Documents / History",
        icon: "📂",
        route: ROUTES.MY_DOCUMENTS,
        roles: ["professional", "corporate"],
      },
    ],
  },
  {
    label: "Collaboration Hub",
    items: [
      {
        id: "collaborators",
        label: "Manage Collaborators",
        icon: "👥",
        route: ROUTES.MANAGE_COLLABORATORS,
        roles: ["corporate"],
      },
      {
        id: "professional-dashboard",
        label: "Clients & Requests",
        icon: "📊",
        route: ROUTES.PROFESSIONAL_DASHBOARD,
        roles: ["professional"],
      },
    ],
  },
  {
    label: "Filing & Calendar",
    items: [
      {
        id: "annual",
        label: "Annual Filing",
        icon: "📅",
        route: ROUTES.ANNUAL_FILING,
        roles: ["professional", "corporate"],
      },
      {
        id: "agmcalc",
        label: "AGM Due Calculator",
        icon: "🗓",
        route: ROUTES.AGM_CALC,
        roles: ["professional", "corporate"],
      },
      {
        id: "calendar",
        label: "Due Calendar",
        icon: "📆",
        route: ROUTES.CALENDAR,
        roles: ["professional", "corporate"],
      },
      {
        id: "mcaforms",
        label: "MCA Forms Help Kit",
        icon: "📑",
        route: ROUTES.MCA_FORMS,
        roles: ["professional"],
      },
      {
        // Prepares the return against our own data; nothing is filed from here.
        id: "mcaprepare",
        label: "Prepare MCA Return",
        icon: "🧾",
        route: ROUTES.MCA_PREPARE,
        roles: ["professional"],
      },
      {
        // Every company keeps these, whatever its size or listing status — the
        // duty under s.88 / s.85 / s.170 / s.189 attaches on incorporation — so
        // unlike the UPSI register this carries no applicability gate.
        id: "registers",
        label: "Statutory Registers",
        icon: "📚",
        route: ROUTES.REGISTERS,
        roles: ["professional", "corporate"],
      },
      {
        // Named for what it holds, not for the statute. "Insider Trading" read
        // as though the feature were for doing it, and "Structured Digital
        // Database" is the legal term of art — neither tells a compliance
        // officer what they will find inside. The statutory name still appears
        // on the page itself and on every export, where a regulator looks.
        id: "sdd",
        label: "UPSI Register",
        icon: "🔒",
        route: ROUTES.SDD,
        roles: ["professional", "corporate"],
        // Hidden only when a company explicitly sets sddApplicable = false.
        // Not keyed on listing status: intermediaries, fiduciaries and IPO-bound
        // companies are unlisted at the moment the Reg 3(5) duty attaches.
        requiresSdd: true,
      },
    ],
  },
  {
    label: "Company Types",
    items: [
      {
        id: "comptype",
        label: "All Company Types",
        icon: "📊",
        route: ROUTES.COMPANY_TYPES,
        roles: ["professional"],
      },
      {
        id: "pvtlimited",
        label: "Private Limited",
        icon: "🏗",
        route: ROUTES.PVT_LIMITED,
        roles: ["professional"],
      },
      {
        id: "llp",
        label: "LLP",
        icon: "🤝",
        route: ROUTES.LLP,
        roles: ["professional"],
      },
      {
        id: "publicco",
        label: "Public / Listed",
        icon: "📈",
        route: ROUTES.PUBLIC_CO,
        roles: ["professional"],
      },
      {
        id: "opc",
        label: "OPC / Startup",
        icon: "👤",
        route: ROUTES.OPC,
        roles: ["professional"],
      },
      {
        id: "ngo",
        label: "Sec 8 / Trust / NGO",
        icon: "🌱",
        route: ROUTES.NGO,
        roles: ["professional"],
      },
    ],
  },
  {
    label: "Business Platform",
    devOnly: true,
    items: [
      {
        id: "marketplace",
        label: "Company Marketplace",
        icon: "🏬",
        route: ROUTES.MARKETPLACE,
        roles: ["professional"],
        devOnly: true,
      },
      {
        id: "valuation",
        label: "Valuation Tools",
        icon: "💰",
        route: ROUTES.VALUATION,
        roles: ["professional"],
        devOnly: true,
      },
      {
        id: "duediligence",
        label: "Due Diligence",
        icon: "🔍",
        route: ROUTES.DUE_DILIGENCE,
        roles: ["professional"],
        devOnly: true,
      },
    ],
  },
  {
    label: "Privacy & Support",
    items: [
      {
        id: "privacy",
        label: "Data Privacy",
        icon: "🔒",
        route: ROUTES.PRIVACY,
        roles: ["professional", "corporate"],
      },
      {
        id: "dpcontrols",
        label: "DP Controls",
        icon: "🛡",
        route: ROUTES.DP_CONTROLS,
        roles: ["professional", "corporate"],
      },
      {
        id: "my-ai-usage",
        label: "My AI Usage",
        icon: "📈",
        route: ROUTES.MY_AI_USAGE,
        roles: ["professional", "corporate"],
      },
      {
        id: "support",
        label: "Help & Support",
        icon: "🎧",
        route: ROUTES.SUPPORT,
        roles: ["professional", "corporate"],
      },
    ],
  },
  {
    label: "Administration",
    items: [
      {
        id: "admin-dash",
        label: "Admin Dashboard",
        icon: "\u2699\uFE0F",
        route: ROUTES.ADMIN_DASHBOARD,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-users",
        label: "User Management",
        icon: "\uD83D\uDC65",
        route: ROUTES.ADMIN_USERS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-services",
        label: "Services",
        icon: "\uD83D\uDD27",
        route: ROUTES.ADMIN_SERVICES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-roles",
        label: "Roles & Permissions",
        icon: "\uD83D\uDD10",
        route: ROUTES.ADMIN_ROLES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-companies",
        label: "Companies",
        icon: "\uD83C\uDFE2",
        route: ROUTES.ADMIN_COMPANIES,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-orders",
        label: "Orders",
        icon: "\uD83D\uDCE6",
        route: ROUTES.ADMIN_ORDERS,
        roles: ["admin", "super_admin"],
      },
      {
        id: "admin-support",
        label: "Support Queue",
        icon: "\uD83C\uDFAB",
        route: ROUTES.ADMIN_SUPPORT_QUEUE,
        roles: ["admin", "super_admin"],
      },
    ],
  },
];
